import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, Home, Menu, Moon, Search, Sun, X } from "lucide-react";
import { EDX } from "@/lib/api";

const LINKS = [
  { to: "/", label: "Home", icon: Home },
  { to: "/capabilities", label: "Services" },
  { to: "/pricing", label: "Pricing" },
  { to: "/the-archive", label: "Our Work" },
  { to: "/the-transmission", label: "About" },
  { to: "/the-observatory", label: "Insights" },
  { to: "/careers", label: "Careers" },
  { to: "/mission-control", label: "Contact" },
];

export default function Navbar({ onOpenPalette }: { onOpenPalette?: () => void }) {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [darkMode, setDarkMode] = useState(true);
  const location = useLocation();
  const isHome = location.pathname === "/";

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <header data-testid="navbar" className={`fixed inset-x-0 top-0 z-40 nav-reference-header ${isHome ? "home-nav-header" : ""}`}>
      <nav className={`nav-shell nav-shell-reference mx-auto ${isHome ? "nav-shell-home" : ""} ${scrolled ? "nav-shell-scrolled" : ""}`}>
        <Link to="/" className="nav-brand" data-testid="nav-logo" aria-label="EDENOX home">
          <img src={EDX.logo} alt="" />
          <span>
            <strong>EDENOX</strong>
            <small>Future. Built today.</small>
          </span>
        </Link>

        <div className="nav-desktop-links hidden items-center lg:flex">
          {LINKS.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              data-testid={`nav-${link.label.toLowerCase().replace(" ", "-")}`}
              className={`nav-link ${location.pathname === link.to ? "nav-link-active" : ""}`}
            >
              {link.icon && <link.icon className="nav-home-icon" aria-hidden="true" />}
              {link.label}
            </Link>
          ))}
        </div>

        <div className="nav-actions flex items-center">
          {onOpenPalette && (
            <button
              type="button"
              data-testid="palette-trigger"
              onClick={onOpenPalette}
              className="nav-search hidden md:inline-flex"
              aria-label="Search the site"
            >
              <Search />
            </button>
          )}
          <button
            type="button"
            className={`nav-theme hidden sm:inline-flex ${darkMode ? "nav-theme-dark" : "nav-theme-light"}`}
            onClick={() => setDarkMode((value) => !value)}
            aria-label={darkMode ? "Dark appearance selected" : "Light appearance selected"}
            aria-pressed={darkMode}
          >
            <span><Sun /></span>
            <span><Moon /></span>
          </button>
          <Link to="/consultation" data-testid="nav-book" className="nav-cta hidden sm:inline-flex">
            Start a project <ArrowRight />
          </Link>
          <button
            type="button"
            data-testid="mobile-menu-btn"
            onClick={() => setOpen((value) => !value)}
            className="nav-menu-button lg:hidden"
            aria-label={open ? "Close navigation" : "Open navigation"}
            aria-expanded={open}
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            className="nav-mobile mx-auto"
            data-testid="mobile-menu"
          >
            {LINKS.map((link, index) => (
              <Link key={link.to} to={link.to}>
                <span>0{index + 1}</span>{link.label}<ArrowRight size={14} />
              </Link>
            ))}
            <Link to="/consultation" className="btn btn-primary mt-3 sm:hidden" data-testid="mobile-book">
              Start a project <ArrowRight size={15} />
            </Link>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
